import { Component, OnInit, ViewChild } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { AlertIcon, AlertService, FormGroupErrorMessageComponent, LoadingPanelService, noWhitespaceValidator, Utils } from 'lib-base';
import { FirmaElectronicaSolicitudService } from '../../services/firmaElectronicaSolicitud.service';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { CommonModule } from '@angular/common';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { IMotivoRevocacion, RevocarFirmaElectronicaDTO } from '../../shared/interfaces/firma_electronica';
import { DropdownModule } from 'primeng/dropdown';
import { MotivoRevocacionService } from '../../services/motivoRevocacion.service';
import { MessageService } from 'primeng/api';
import { firstValueFrom, forkJoin } from 'rxjs';
import { SolicitudRevocacionService } from '../../services/solicitudRevocacion.service';
import { Router } from '@angular/router';
import { FirmaElectronicaTokenService } from '../../services/firmaElectronicaToken.service';

@Component({
  selector: 'app-revocar-firma-electronica',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, TranslateModule, ButtonModule, TooltipModule, FormGroupErrorMessageComponent, DropdownModule,
    IconFieldModule, InputIconModule, InputTextareaModule],
  template: `
  <div class="p-grid p-justify-center p-align-start wrapper-content flex-column mt-4 mb-5">
    <div class="col-12 px-0">
      <div class="app-card">
        <h1 class="card-header title-blue font-roboto-condensed  font-23 font-normal">{{'components.app-pub-topbar.firma-electronica-revocacion-long' | translate}}</h1>
        <div class="card-body">
          <form [formGroup]="form" (ngSubmit)="onSubmit()">
            <div class="flex flex-column gap-2 px-3">
              <main class="grid formgrid p-fluid">
              <div class="field col-12">
                  <label class="block mb-2 font-medium color-form-label">{{'common.motivoRevocacion' | translate}}
                    <span class="text-red-500">*</span>
                  </label>
                  <p-dropdown [options]="motivosRevocacion" formControlName="motivoRevocacionId" 
                    class="container-dropdown-input" [style]="{'width':'100%'}" autoWidth="false" appendTo="body"
                    [placeholder]="'common.motivoRevocacion' | translate" 
                    optionLabel="descripcion" optionValue="id">
                  </p-dropdown>
                  <ng-container *ngIf="form.get('motivoRevocacionId')?.invalid && form.get('motivoRevocacionId')?.dirty">
                    <app-form-group-error-message [control]="form.get('motivoRevocacionId')"/>
                  </ng-container>
                </div>                
                <div class="field col-12">
                  <label class="block mb-2 font-medium color-form-label">{{'common.observacion' | translate}}
                    <span class="text-red-500">*</span>
                  </label>
                  <textarea rows="5" cols="30" 
                    pInputTextarea 
                    formControlName="justificacionRevocacion">
                  </textarea>
                  <ng-container *ngIf="form.get('justificacionRevocacion')?.invalid && form.get('justificacionRevocacion')?.touched">
                    <app-form-group-error-message [control]="form.get('justificacionRevocacion')"/>
                  </ng-container>
                </div>
                <div class="col-12">
                  <div [pTooltip]="'msg.esteUsuarioNoTieneFirmaElectronica' | translate" tooltipPosition="top" [tooltipDisabled]="!btnDisabled">
                    <button pButton [label]="'msg.firmaElectronica-revocar-btn' | translate" type="submit"
                            [disabled]="btnDisabled"
                            class="bg-button-outline-blue-light  border-outline-blue-light text-blue-general font-normal	w-full"></button>
                  </div>
                </div>
              </main>
            </div>
          </form>          
        </div>
      </div>
    </div>
  </div>
  `
})
export class RevocarFirmaElectronicaComponent implements OnInit {

  btnDisabled: boolean = false;
  trans: any = {};
  form !: FormGroup;
  motivosRevocacion !: IMotivoRevocacion[];

  constructor(private _fb: FormBuilder, private _lps: LoadingPanelService, private _fes: FirmaElectronicaSolicitudService, private _mrs: MotivoRevocacionService,
    private readonly _ms: MessageService, private readonly _t: TranslateService, private _srs: SolicitudRevocacionService, private _as: AlertService,
    private _r: Router, private _fet: FirmaElectronicaTokenService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.translate();
    this.getMotivosRevocacion();
    this.usuarioTieneFirmaElectronicaActiva();
  }

  private async translate(): Promise<void> {
    this.trans = await firstValueFrom(forkJoin({
      warnSummary: this._t.get('form.msg.warn-summary'),
      completeCampos: this._t.get('form.msg.complete-campos'),
      aceptar: this._t.get('form.button.aceptar'),
      firmaElectronicaRevocacionLong: this._t.get('components.app-pub-topbar.firma-electronica-revocacion-long'),
      solicitudRevocacionCreada: this._t.get('msg.solicitudRevocacionCreada')
    }));
  }

  private initForm() {
    this.form = this._fb.group({
      motivoRevocacionId: [null, [Validators.required]],
      justificacionRevocacion: [null, [Validators.required, noWhitespaceValidator, Validators.maxLength(255)]],
    })
  }

  onSubmit() {
    if (this.form.invalid) {
      Utils.markAllFormControlsAsTouched(this.form);
      this._ms.add({ severity: 'warn', summary: this.trans.warnSummary, detail: this.trans.completeCampos });
      return;
    }

    let request: RevocarFirmaElectronicaDTO = {
      motivoRevocacionId: this.form.get('motivoRevocacionId')?.value,
      justificacionRevocacion: this.form.get('justificacionRevocacion')?.value,
    }

    this.crearSolicitudRevocarFirmaElectronica(request);
  }

  getMotivosRevocacion() {
    this._lps.show(true);
    return this._mrs.getMotivosRevocacion().subscribe({
      next: (data: IMotivoRevocacion[]) => {
        this.motivosRevocacion = data;
      },
      complete: () => {
        this._lps.hide();
      }
    });
  }

  crearSolicitudRevocarFirmaElectronica(request: RevocarFirmaElectronicaDTO) {
    this._lps.show(true)
    this._srs.crearSolicitudRevocarFirmaElectronica(request).subscribe({
      next: (response: boolean) => {
        this._lps.hide();
        this._as.show({
          icon: AlertIcon.SUCCESS,
          title: this.trans.firmaElectronicaRevocacionLong,
          html: this.trans.solicitudRevocacionCreada,
          showAccept: true,
          textAccept: this.trans.aceptar,
        }).subscribe((event)=>{
          this._r.navigate(['/public/consulta']);
        });
      }, complete: () => {
        this._lps.hide();
      }
    });
  }

  usuarioTieneFirmaElectronicaActiva() {
    this._lps.show(true);
    this._fet.usuarioTieneFirmaElectronicaActiva().subscribe({
      next: (response: boolean) => {
        this.btnDisabled = !response;
      }, complete: () => {
        this._lps.hide();
      }
    });
  }
}
